import { ExtraParamsKey, SignInIdentifier } from '@logto/schemas';
import classNames from 'classnames';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';

import CaptchaContext from '@/Providers/CaptchaContextProvider/CaptchaContext';
import UserInteractionContext from '@/Providers/UserInteractionContextProvider/UserInteractionContext';
import {
  identifyUser,
  submitInteraction,
  updateProfile,
  verifyVerificationCode,
} from '@/apis/experience';
import { sendVerificationCodeApi } from '@/apis/utils';
import { InputField, PasswordInputField } from '@/components/InputFields';
import CaptchaBox from '@/containers/CaptchaBox';
import useErrorHandler from '@/hooks/use-error-handler';
import useGlobalRedirectTo from '@/hooks/use-global-redirect-to';
import usePasswordPolicyChecker from '@/hooks/use-password-policy-checker';
import { usePasswordPolicy } from '@/hooks/use-sie';
import ErrorMessage from '@/shared/components/ErrorMessage';
import { UserFlow } from '@/types';

import styles from './index.module.scss';

type Props = {
  readonly className?: string;
  // eslint-disable-next-line react/boolean-prop-naming
  readonly autoFocus?: boolean;
};

type FormState = {
  email: string;
  code: string;
  password: string;
  inviteCode: string;
};

const invitationCookieName = 'medra_invite';

const readInviteCookie = () => {
  const prefix = `${invitationCookieName}=`;
  const item = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix));

  return item ? decodeURIComponent(item.slice(prefix.length)) : '';
};

const normalizeInviteCode = (value: string) =>
  value
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9_-]/g, '')
    .slice(0, 32);

const requiredLabel = (label: string) => (
  <>
    <span className={styles.requiredMark}>*</span>
    {label}
  </>
);

const persistInviteCode = (value: string) => {
  const inviteCode = normalizeInviteCode(value);
  const { hostname, protocol } = window.location;
  const domain = hostname === 'medra.vip' || hostname.endsWith('.medra.vip')
    ? '; Domain=.medra.vip'
    : '';
  const secure = protocol === 'https:' ? '; Secure' : '';
  const maxAge = inviteCode ? 60 * 60 : 0;

  document.cookie = `${invitationCookieName}=${encodeURIComponent(
    inviteCode
  )}; Path=/; Max-Age=${maxAge}; SameSite=Lax${domain}${secure}`;
};

const EmailRegisterForm = ({ className, autoFocus }: Props) => {
  const [searchParams] = useSearchParams();
  const initialInviteCode = useMemo(
    () =>
      normalizeInviteCode(
        searchParams.get(ExtraParamsKey.InviteCode) ?? readInviteCookie()
      ),
    [searchParams]
  );
  const [verificationId, setVerificationId] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [passwordError, setPasswordError] = useState<string>();

  const { executeCaptcha } = useContext(CaptchaContext);
  const { setIdentifierInputValue } = useContext(UserInteractionContext);
  const handleError = useErrorHandler();
  const redirectTo = useGlobalRedirectTo();
  const checkPassword = usePasswordPolicyChecker({ setErrorMessage: setPasswordError });
  const {
    policy: {
      length: { max: passwordMaxLength },
    },
  } = usePasswordPolicy();

  const {
    control,
    register,
    getValues,
    setError,
    clearErrors,
    trigger,
    handleSubmit,
    formState: { errors },
  } = useForm<FormState>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      email: '',
      code: '',
      password: '',
      inviteCode: initialInviteCode,
    },
  });

  useEffect(() => {
    // The invite cookie only carries the code into this Logto interaction.
    // Consume it here so a later direct registration does not inherit stale data.
    persistInviteCode('');
  }, []);

  useEffect(() => {
    if (countdown <= 0) {
      return;
    }

    const timer = window.setInterval(() => {
      setCountdown((value) => Math.max(0, value - 1));
    }, 1000);

    return () => {
      window.clearInterval(timer);
    };
  }, [countdown]);

  const showApiError = useCallback(
    async (error: unknown, target: 'code' | 'form' = 'form') => {
      await handleError(error, {
        'verification_code.expired': ({ message }) => {
          setError('code', { message });
        },
        'verification_code.code_mismatch': ({ message }) => {
          setError('code', { message });
        },
        'user.email_already_in_use': () => {
          setError('email', { message: '该邮箱已注册，请返回登录。' });
        },
        global: ({ message }) => {
          if (target === 'code') {
            setError('code', { message });
            return;
          }
          setFormError(message);
        },
      });
    },
    [handleError, setError]
  );

  const sendCode = useCallback(async () => {
    const emailIsValid = await trigger('email');
    if (!emailIsValid || countdown > 0 || isSendingCode) {
      return;
    }

    const email = getValues('email').trim();
    setFormError('');
    clearErrors('code');
    setIsSendingCode(true);

    try {
      const captchaToken = await executeCaptcha();
      const result = await sendVerificationCodeApi(
        UserFlow.Register,
        { type: SignInIdentifier.Email, value: email },
        undefined,
        captchaToken
      );
      setIdentifierInputValue({ type: SignInIdentifier.Email, value: email });
      setVerificationId(result.verificationId);
      setCountdown(60);
    } catch (error: unknown) {
      await showApiError(error, 'code');
    } finally {
      setIsSendingCode(false);
    }
  }, [
    clearErrors,
    countdown,
    executeCaptcha,
    getValues,
    isSendingCode,
    setIdentifierInputValue,
    showApiError,
    trigger,
  ]);

  const onSubmit = useCallback(
    async ({ email, code, password }: FormState) => {
      setFormError('');
      setPasswordError(undefined);
      if (!verificationId) {
        setError('code', { message: '请先发送验证码。' });
        return;
      }

      if (!(await checkPassword(password))) {
        return;
      }

      setIsSubmitting(true);
      try {
        const verified = await verifyVerificationCode({
          verificationId,
          identifier: { type: SignInIdentifier.Email, value: email.trim() },
          code: code.trim(),
        });

        await updateProfile({ type: 'password', value: password });
        await identifyUser({ verificationId: verified.verificationId });
        const result = await submitInteraction();
        await redirectTo(result.redirectTo);
      } catch (error: unknown) {
        await showApiError(error);
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      checkPassword,
      redirectTo,
      setError,
      showApiError,
      verificationId,
    ]
  );

  return (
    <form className={classNames(styles.form, className)} onSubmit={handleSubmit(onSubmit)}>
      <InputField
        autoFocus={autoFocus}
        autoComplete="email"
        className={styles.inputField}
        label={requiredLabel('邮箱')}
        placeholder="请输入邮箱地址"
        isDanger={Boolean(errors.email)}
        errorMessage={errors.email?.message}
        {...register('email', {
          required: '请输入邮箱地址。',
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: '请输入有效的邮箱地址。',
          },
          onChange: () => {
            setVerificationId('');
            setCountdown(0);
            clearErrors('code');
          },
        })}
      />

      <div className={styles.codeField}>
        <label htmlFor="register-verification-code">
          <span className={styles.requiredMark}>*</span>
          验证码
        </label>
        <div className={classNames(styles.codeControl, errors.code && styles.danger)}>
          <input
            id="register-verification-code"
            autoComplete="one-time-code"
            inputMode="numeric"
            maxLength={6}
            placeholder="输入邮箱验证码"
            {...register('code', {
              required: '请输入邮箱验证码。',
              pattern: {
                value: /^\d{6}$/,
                message: '请输入 6 位邮箱验证码。',
              },
            })}
          />
          <button
            type="button"
            disabled={countdown > 0 || isSendingCode}
            onClick={sendCode}
          >
            {isSendingCode ? '发送中' : countdown > 0 ? `${countdown}s` : '发送验证码'}
          </button>
        </div>
        {errors.code?.message && (
          <ErrorMessage className={styles.fieldError}>{errors.code.message}</ErrorMessage>
        )}
      </div>

      <PasswordInputField
        autoComplete="new-password"
        className={styles.inputField}
        label={requiredLabel('设置密码')}
        placeholder="至少 8 位，建议包含字母和数字"
        maxLength={passwordMaxLength}
        isDanger={Boolean(errors.password) || Boolean(passwordError)}
        errorMessage={errors.password?.message ?? passwordError}
        {...register('password', {
          required: '请设置登录密码。',
          onChange: () => {
            setPasswordError(undefined);
          },
        })}
      />

      <Controller
        control={control}
        name="inviteCode"
        rules={{
          pattern: {
            value: /^[A-Za-z0-9_-]{0,32}$/,
            message: '邀请码格式不正确。',
          },
        }}
        render={({ field }) => (
          <InputField
            {...field}
            autoComplete="off"
            className={styles.inputField}
            label="邀请码"
            placeholder="选填，如 MEDRA-8K27Q"
            isDanger={Boolean(errors.inviteCode)}
            errorMessage={errors.inviteCode?.message}
            onChange={(event) => {
              const inviteCode = normalizeInviteCode(event.currentTarget.value);
              field.onChange(inviteCode);
            }}
          />
        )}
      />

      <CaptchaBox />
      {formError && <ErrorMessage className={styles.formError}>{formError}</ErrorMessage>}

      <button
        className={styles.submitButton}
        disabled={isSubmitting}
        type="submit"
      >
        {isSubmitting ? '注册中...' : '注册并登录'}
      </button>
    </form>
  );
};

export default EmailRegisterForm;
